import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { HomeFooter } from '../components/home/HomeFooter';
import { HomeHeader } from '../components/home/HomeHeader';
import { loadAuthSession } from '../data/authSession';
import { resolveProfileImageUrl } from '../config/media';
import {
  getBand,
  getBandMembers,
  REGION_LABELS,
  GENRE_LABELS,
  type BandResponse,
  type BandMemberResponse,
} from '../api/bandworld';

export function BandDetailPage() {
  const navigate = useNavigate();
  const { bandId } = useParams<{ bandId: string }>();
  const isAuthenticated = Boolean(loadAuthSession());

  const [band, setBand] = useState<BandResponse | null>(null);
  const [members, setMembers] = useState<BandMemberResponse[]>([]);
  const [bandLoading, setBandLoading] = useState(true);
  const [membersLoading, setMembersLoading] = useState(true);
  const [bandError, setBandError] = useState<string | null>(null);
  const [membersError, setMembersError] = useState<string | null>(null);

  useEffect(() => {
    if (!bandId) return;
    let cancelled = false;

    setBandLoading(true);
    setBandError(null);
    getBand(bandId)
      .then((res) => {
        if (cancelled) return;
        setBand(res);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setBandError(err.message ?? '밴드 정보를 불러오지 못했습니다.');
      })
      .finally(() => {
        if (!cancelled) setBandLoading(false);
      });

    setMembersLoading(true);
    setMembersError(null);
    getBandMembers(bandId)
      .then((res) => {
        if (cancelled) return;
        setMembers(res);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setMembersError(err.message ?? '멤버 정보를 불러오지 못했습니다.');
      })
      .finally(() => {
        if (!cancelled) setMembersLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [bandId]);

  const loading = bandLoading;

  if (loading) {
    return (
      <main className="band-detail-page">
        <HomeHeader authenticated={isAuthenticated} onGuestCta={() => navigate('/login')} variant="icon" />
        <section className="band-detail-page__inner">
          <div className="band-detail-page__loading">로딩 중...</div>
        </section>
        <HomeFooter />
      </main>
    );
  }

  if (bandError || !band) {
    return (
      <main className="band-detail-page">
        <HomeHeader authenticated={isAuthenticated} onGuestCta={() => navigate('/login')} variant="icon" />
        <section className="band-detail-page__inner">
          <div className="band-detail-page__error">
            {bandError ?? '밴드 정보를 불러올 수 없습니다.'}
          </div>
          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <button
              onClick={() => navigate(-1)}
              style={{ minHeight: '44px', padding: '0.5rem 1.5rem', borderRadius: '8px', border: '1px solid #ccc', background: '#fff', cursor: 'pointer' }}
              type="button"
            >
              뒤로 가기
            </button>
          </div>
        </section>
        <HomeFooter />
      </main>
    );
  }

  return (
    <main className="band-detail-page">
      <HomeHeader authenticated={isAuthenticated} onGuestCta={() => navigate('/login')} variant="icon" />

      <section className="band-detail-page__inner">
        <button className="band-detail-page__back" onClick={() => navigate(-1)} type="button">
          ← 목록으로
        </button>

        <div className="band-detail-page__header">
          {band.imageUrl ? (
            <img
              alt={band.name}
              className="band-detail-page__cover"
              src={band.imageUrl}
            />
          ) : null}
          <h1 className="band-detail-page__name">{band.name}</h1>

          <div className="band-detail-page__chips">
            {band.region ? (
              <span className="band-detail-page__chip">
                {REGION_LABELS[band.region] ?? band.region}
              </span>
            ) : null}
            {band.genre ? (
              <span className="band-detail-page__chip">
                {GENRE_LABELS[band.genre] ?? band.genre}
              </span>
            ) : null}
          </div>

          {band.introduction ? (
            <p className="band-detail-page__intro">{band.introduction}</p>
          ) : null}
        </div>

        <div className="band-detail-page__section">
          <h2 className="band-detail-page__section-title">밴드 정보</h2>
          <dl className="band-detail-page__info-list">
            {band.region ? (
              <div className="band-detail-page__info-row">
                <dt className="band-detail-page__info-label">활동 지역</dt>
                <dd className="band-detail-page__info-value">
                  {REGION_LABELS[band.region] ?? band.region}
                </dd>
              </div>
            ) : null}
            {band.genre ? (
              <div className="band-detail-page__info-row">
                <dt className="band-detail-page__info-label">장르</dt>
                <dd className="band-detail-page__info-value">
                  {GENRE_LABELS[band.genre] ?? band.genre}
                </dd>
              </div>
            ) : null}
            <div className="band-detail-page__info-row">
              <dt className="band-detail-page__info-label">멤버</dt>
              <dd className="band-detail-page__info-value">
                {band.memberCount}/{band.capacity}명
              </dd>
            </div>
          </dl>
        </div>

        <div className="band-detail-page__section">
          <h2 className="band-detail-page__section-title">
            멤버 {membersLoading ? '' : `(${members.length}명)`}
          </h2>

          {membersLoading ? (
            <div className="band-detail-page__loading">멤버 정보를 불러오는 중...</div>
          ) : membersError ? (
            <div className="band-detail-page__error">{membersError}</div>
          ) : members.length === 0 ? (
            <div className="band-detail-page__loading" style={{ color: '#888' }}>
              멤버 정보가 없습니다.
            </div>
          ) : (
            <ul className="band-detail-page__member-list">
              {members.map((member) => {
                const avatarSrc = resolveProfileImageUrl(null, member.profile.profileImageUrl);
                const isLeader = member.role === 'LEADER';
                return (
                  <li className="band-detail-page__member-card" key={member.userId}>
                    {avatarSrc ? (
                      <img
                        alt={member.profile.nickname}
                        className="band-detail-page__member-avatar"
                        src={avatarSrc}
                      />
                    ) : (
                      <div className="band-detail-page__member-avatar--placeholder" aria-hidden="true" />
                    )}
                    <div className="band-detail-page__member-info">
                      <span className="band-detail-page__member-nickname">
                        {member.profile.nickname}
                      </span>
                      <span
                        className={`band-detail-page__member-role${isLeader ? ' band-detail-page__member-role--leader' : ''}`}
                      >
                        {isLeader ? '리더' : '멤버'}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>

      <HomeFooter />
    </main>
  );
}
